"use client"
import React from "react"
import { useState, useEffect, useRef } from "react"
import moment from "moment"
import { Typography, Button, Row, Col, message, Spin, Card, Tooltip } from "antd"
import { useSelector, useDispatch } from "react-redux"
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from "recharts"
import { ReloadOutlined, CloseOutlined,RiseOutlined, FallOutlined } from "@ant-design/icons"
import ScoreDrawer from "./score-drawer"
import styled from "styled-components"

import {assignPracticeAssessment} from 'utils/assignPracticeAssessment'
import {
  fetchAptitudePracticeAssessments,
  fetchReports,
  generatePDFReport,
  // checkRetakeEligibility,
  fetchAptitudePracticeScores,
} from "../../actions"
import selectors, { selectAptitudeDayZero } from "../../selectors"

// Import Aptitude Assessment Components
import AptitudeInstructions from "../../Partials/aptitudeassmt/instruction"
import AptitudeAssessment from "../../Partials/aptitudeassmt/assessment"
import AptitudeCompletion from "../../Partials/aptitudeassmt/completion"
import { trackPracticeSessionStarted } from "../../../../utils/assessmentEvents"

const { Title, Text } = Typography

// Demo chart data for preview
const demoChartData = [
  { session: 0, Quantitative: 0, Logical: 0, Critical: 0, "Combined Progress": 0 },
  { session: 1, Quantitative: 22, Logical: 18, Critical: 15, "Combined Progress": 18 },
  { session: 2, Quantitative: 38, Logical: 32, Critical: 28, "Combined Progress": 33 },
  { session: 3, Quantitative: 52, Logical: 45, Critical: 42, "Combined Progress": 46 },
  { session: 4, Quantitative: 64, Logical: 58, Critical: 55, "Combined Progress": 59 },
  { session: 5, Quantitative: 74, Logical: 68, Critical: 65, "Combined Progress": 69 },
  { session: 6, Quantitative: 82, Logical: 76, Critical: 73, "Combined Progress": 77 },
  { session: 7, Quantitative: 88, Logical: 82, Critical: 79, "Combined Progress": 83 },
]

// Professional styled components
const StyledAptitudeContainer = styled.div`
  padding: 24px;
  min-height: 100vh;
`

const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  background-color: #ffffff;
  border-radius: 8px;
  margin-bottom: 24px;
  border: 1px solid #e8e8e8;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
`

const PageTitle = styled(Title)`
  color: #262626 !important;
  margin-bottom: 0 !important;
  font-size: 20px !important;
  font-weight: 600 !important;
`

const ContinueButton = styled(Button)`
  background-color: #1890ff;
  border-color: #1890ff;
  color: #ffffff;
  font-weight: 500;
  height: 40px;
  padding: 0 24px;
  border-radius: 6px;
  &:hover {
    background-color: #40a9ff;
    color: #ffffff;
    border-color: #40a9ff;
  }
  &:focus {
    background-color: #40a9ff;
    border-color: #40a9ff;
  }
  &:disabled {
    background-color: #f5f5f5;
    border-color: #d9d9d9;
    color: #bfbfbf;
  }
`

// Section with overlay loader support
const SectionWithOverlay = styled.div`
  position: relative;
  margin-bottom: 24px;
  background-color: #ffffff;
  border-radius: 8px;
  padding: 24px;
  border: 1px solid #e8e8e8;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  min-height: ${(props) => props.minHeight || "auto"};
  
  &.has-overlay {
    .section-content {
      filter: blur(2px);
      opacity: 0.6;
      pointer-events: none;
    }
  }
`

const SectionOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(4px);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 10;
  gap: 16px;
  
  .overlay-spinner {
    .ant-spin-dot {
      font-size: 24px;
    }
  }
  
  .overlay-text {
    color: #595959;
    font-size: 15px;
    font-weight: 600;
    text-align: center;
  }
`

const VisualizationSection = styled(SectionWithOverlay)`
  min-height: 500px;
`

const ChartContainer = styled.div`
  position: relative;
  margin-bottom: 24px;
  height: 400px;
  background: #ffffff;
  border-radius: 6px;
  border: 1px solid #f0f0f0;
  padding: 16px;
  overflow: hidden;

  &.demo-mode {
    pointer-events: none;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      backdrop-filter: blur(2px);
      background: rgba(255, 255, 255, 0.6);
      z-index: 1;
    }

    &::after {
      display: none; // Hide the default message
    }

    .baseline-overlay {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: rgba(255, 255, 255, 0.95);
      padding: 24px 32px;
      border-radius: 8px;
      text-align: center;
      border: 1px solid #e8e8e8;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
      z-index: 2;
      min-width: 300px;

      .title {
        font-size: 18px;
        font-weight: 600;
        color: #262626;
        margin-bottom: 12px;
      }

      .description {
        font-size: 14px;
        color: #8c8c8c;
        margin-bottom: 16px;
      }

      .scores {
        color: #1890ff;
        font-size: 14px;
        display: flex;
        flex-direction: column;
        gap: 8px;
        margin-top: 16px;
        text-align: left;

        .score-item {
          display: flex;
          justify-content: space-between;
          gap: 24px;

          &.total {
            margin-top: 8px;
            padding-top: 8px;
            border-top: 1px dashed #d9d9d9;
            font-weight: 600;
          }
        }
      }
    }
  }
`


const ProgressSection = styled.div`
  background-color: #fafafa;
  border-radius: 6px;
  padding: 20px;
  text-align: center;
  border: 1px solid #f0f0f0;
`

const ProgressTitle = styled(Title)`
  color: #262626 !important;
  font-size: 16px !important;
  margin-bottom: 16px !important;
  font-weight: 600 !important;
`

const ProgressValue = styled(Text)`
  font-size: 20px;
  font-weight: 600;
  color: #52c41a;
  margin-left: 8px;
`

const HistorySection = styled(SectionWithOverlay)`
  min-height: 300px;
  margin-top: 24px;
`

const HistoryHeader = styled(Title)`
  color: #262626 !important;
  font-size: 18px !important;
  margin-bottom: 8px;
  font-weight: 600;
`

const SubHeaderText = styled(Text)`
  color: #8c8c8c;
  font-size: 14px;
  display: block;
  margin-bottom: 20px;
`

const OldPracticeItem = styled.div`
  background-color: #ffffff;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  margin-bottom: 16px;
  padding: 20px 24px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  &:hover {
    border-color: rgb(134, 134, 134);
    box-shadow: 0 4px 16px rgba(24, 144, 255, 0.1);
  }
  &:last-child {
    margin-bottom: 0;
  }
`

const OldPracticeContent = styled(Row)`
  align-items: center;
  min-height: 60px;
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`

const PracticeItemLeft = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  gap: 12px;
  @media (max-width: 768px) {
    width: 100%;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`

const BadgeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  @media (max-width: 768px) {
    width: 100%;
    justify-content: flex-start;
  }
`

const DateBadge = styled.span`
  background: linear-gradient(135deg, #52c41a 0%, #73d13d 100%);
  color: #ffffff;
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.5px;
  white-space: nowrap;
`

const LatestBadge = styled.span`
  background: linear-gradient(135deg, #1890ff 0%, #40a9ff 100%);
  color: white;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  white-space: nowrap;
`

const OldPracticeTitle = styled.div`
  color: rgb(59, 59, 59);
  font-size: 15px;
  font-weight: 600;
  line-height: 1.4;
  flex: 1;
  min-width: 0;
  @media (max-width: 768px) {
    font-size: 14px;
    margin-top: 4px;
  }
`

const ScoresContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
  @media (max-width: 1200px) {
    gap: 12px;
  }
  @media (max-width: 768px) {
    width: 100%;
    justify-content: space-between;
    gap: 8px;
    padding-top: 12px;
    border-top: 1px solid #f0f0f0;
  }
  @media (max-width: 480px) {
    flex-wrap: wrap;
    gap: 8px;
  }
`

const ScoreItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  min-width: 80px;
  .label {
    font-size: 11px;
    color: #8c8c8c;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 2px;
  }
  .value {
    font-size: 13px;
    color: #262626;
    font-weight: 600;
    line-height: 1.2;
  }
  @media (max-width: 768px) {
    min-width: 70px;
    .label {
      font-size: 10px;
    }
    .value {
      font-size: 12px;
    }
  }
`

const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  @media (max-width: 480px) {
    width: 100%;
    justify-content: center;
    margin-top: 8px;
  }
`

const ViewScoreButton = styled(Button)`
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border: 1px solid #d9d9d9;
  color: #595959;
  font-size: 12px;
  font-weight: 500;
  padding: 4px 16px;
  height: 32px;
  border-radius: 4px;
  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #1890ff 0%, #40a9ff 100%);
    border-color: #1890ff;
    color: #ffffff;
    box-shadow: 0 4px 12px rgba(24, 144, 255, 0.3);
  }
  &:focus:not(:disabled) {
    background: linear-gradient(135deg, #1890ff 0%, #40a9ff 100%);
    border-color: #1890ff;
    color: #ffffff;
  }
  &:disabled {
    background: #f5f5f5;
    border-color: #d9d9d9;
    color: #bfbfbf;
    cursor: not-allowed;
  }
`

const RetakeButton = styled(Button)`
  background: linear-gradient(135deg, #52c41a 0%, #73d13d 100%);
  border: none;
  color: #ffffff;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 16px;
  height: 32px;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(82, 196, 26, 0.2);
  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #73d13d 0%, #95de64 100%);
    box-shadow: 0 4px 12px rgba(82, 196, 26, 0.4);
    color: #ffffff;
  }
  &:focus:not(:disabled) {
    background: linear-gradient(135deg, #73d13d 0%, #95de64 100%);
    color: #ffffff;
  }
  &:disabled {
    background: #f5f5f5;
    border: 1px solid #d9d9d9;
    color: #bfbfbf;
    cursor: not-allowed;
    box-shadow: none;
  }
  @media (max-width: 768px) {
    font-size: 11px;
    padding: 4px 12px;
    height: 30px;
  }
`

// Practice selection interface styled components
const PracticeSelectionSection = styled(SectionWithOverlay)`
  min-height: 200px;
`

const PracticeHeader = styled.div`
  margin-bottom: 24px;
  text-align: center;
`

const PracticeTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #262626;
  margin-bottom: 8px;
  margin-top: 0;
`

const PracticeSubtitle = styled.p`
  font-size: 14px;
  color: #8c8c8c;
  margin: 0;
`

const OptionsContainer = styled.div`
  display: flex;
  gap: 16px;
  max-width: 800px;
  margin: 0 auto;
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
  }
`

const OptionBox = styled.div`
  flex: 1;
  background: #ffffff;
  border: 2px solid #f0f0f0;
  border-radius: 6px;
  padding: 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  min-height: 120px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  &:hover {
    border-color: #1890ff;
    box-shadow: 0 4px 12px rgba(24, 144, 255, 0.1);
  }
`

const OptionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #262626;
  margin-bottom: 8px;
  margin-top: 0;
`

const OptionDescription = styled.p`
  font-size: 13px;
  color: #595959;
  margin: 0;
  line-height: 1.4;
`

const RecommendedBadge = styled.span`
  position: absolute;
  top: 8px;
  right: 8px;
  background: #52c41a;
  color: white;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
`

const OrText = styled.p`
  font-size: 11px;
  color: #fa8c16;
  margin: 6px 0 0 0;
  font-weight: 500;
`

// Modal styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  @media (max-width: 768px) {
    padding: 10px;
  }
`

const ModalContainer = styled.div`
  background: white;
  border-radius: 8px;
  width: 100%;
  max-width: 900px;
  height: 80vh;
  max-height: 700px;
  min-height: 600px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  border: 1px solid #e8e8e8;
  @media (max-width: 768px) {
    width: 100%;
    height: 90vh;
    max-height: none;
    border-radius: 6px;
  }
`

const ModalHeader = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #fafafa;
  flex-shrink: 0;
  @media (max-width: 768px) {
    padding: 16px 20px;
  }
`

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #262626;
  @media (max-width: 768px) {
    font-size: 16px;
  }
`

const CloseButton = styled.button`
  background: white;
  border: 1px solid #d9d9d9;
  font-size: 16px;
  cursor: pointer;
  color: #8c8c8c;
  padding: 6px;
  width: 32px;
  height: 32px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  &:hover {
    background: #f5f5f5;
    border-color: #bfbfbf;
    color: #595959;
  }
`

const ModalBody = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
  min-height: 0;
`

const Sidebar = styled.div`
  width: 200px;
  background: #fafafa;
  border-right: 1px solid #f0f0f0;
  overflow-y: auto;
  flex-shrink: 0;
  @media (max-width: 768px) {
    width: 160px;
  }
  @media (max-width: 480px) {
    width: 140px;
  }
`

const SidebarTab = styled.div`
  position: relative;
  padding: 16px 20px;
  cursor: pointer;
  font-size: 14px;
  font-weight: ${(props) => (props.active ? "600" : "500")};
  color: ${(props) => (props.active ? "#262626" : "#8c8c8c")};
  background: ${(props) => (props.active ? "white" : "transparent")};
  border-radius: ${(props) => (props.active ? "6px 0 0 6px" : "0")};
  margin: ${(props) => (props.active ? "4px 0" : "4px 0")};
  transition: all 0.2s ease;
  line-height: 1.4;
  ${(props) =>
    props.active &&
    `
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    border-right: 2px solid #1890ff;
  `}
  &:hover {
    background: ${(props) => (props.active ? "white" : "#f5f5f5")};
    color: ${(props) => (props.active ? "#262626" : "#595959")};
  }
  ${(props) =>
    props.hasSelectedTopics &&
    !props.active &&
    `
    &::after {
      content: '${props.selectedCount}';
      position: absolute;
      top: 8px;
      right: 8px;
      width: 18px;
      height: 18px;
      background: #1890ff;
      color: white;
      border-radius: 50%;
      font-size: 10px;
      font-weight: 600;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  `}
  ${(props) =>
    props.hasSelectedTopics &&
    props.active &&
    `
    &::after {
      content: '${props.selectedCount}';
      position: absolute;
      top: 8px;
      right: 6px;
      width: 20px;
      height: 20px;
      background: #1890ff;
      color: white;
      border-radius: 50%;
      font-size: 11px;
      font-weight: 600;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  `}
  
  @media (max-width: 768px) {
    padding: 14px 16px;
    font-size: 13px;
  }
`

const MainContent = styled.div`
  flex: 1;
  padding: 24px;
  overflow-y: auto;
  min-height: 0;
  background: #ffffff;
  @media (max-width: 768px) {
    padding: 20px;
  }
`

const TopicsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 12px;
  margin-bottom: 20px;
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 10px;
  }
`

const TopicItem = styled.label`
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border: 2px solid
    ${(props) => (props.isDisabled ? "#e5e7eb" : props.isSelected ? "#1890ff" : "#f0f0f0")};
  border-radius: 6px;
  cursor: ${(props) => (props.isDisabled ? "not-allowed" : "pointer")};
  transition: all 0.2s ease;
  font-size: 14px;
  background: ${(props) => (props.isDisabled ? "#f9fafb" : props.isSelected ? "#eff6ff" : "white")};
  line-height: 1.4;
  min-height: 50px;
  opacity: ${(props) => (props.isDisabled ? 0.6 : 1)};
  user-select: none;
  &:hover {
    border-color: ${(props) => (props.isDisabled ? "#e5e7eb" : props.isSelected ? "#2563eb" : "#1890ff")};
    transform: ${(props) => (props.isDisabled ? "none" : "translateY(-1px)")};
    box-shadow: ${(props) => (props.isDisabled ? "none" : "0 2px 4px rgba(0, 0, 0, 0.05)")};
  }
  &:active {
    transform: ${(props) => (props.isDisabled ? "none" : "translateY(0)")};
  }
  input[type='checkbox'] {
    appearance: none;
    width: 18px;
    height: 18px;
    border: 2px solid ${(props) => (props.isDisabled ? "#d1d5db" : "#d9d9d9")};
    border-radius: 4px;
    background: white;
    cursor: ${(props) => (props.isDisabled ? "not-allowed" : "pointer")};
    flex-shrink: 0;
    position: relative;
    pointer-events: none;
    &::after {
      content: '';
      position: absolute;
      top: 2px;
      left: 5px;
      width: 4px;
      height: 8px;
      border: solid white;
      border-width: 0 2px 2px 0;
      transform: rotate(45deg) scale(0);
      transition: transform 0.2s ease;
    }
    &:checked {
      background: ${(props) => (props.isDisabled ? "#9ca3af" : "#1890ff")};
      border-color: ${(props) => (props.isDisabled ? "#9ca3af" : "#1890ff")};
      &::after {
        transform: rotate(45deg) scale(1);
      }
    }
    &:focus {
      outline: none;
      box-shadow: ${(props) => (props.isDisabled ? "none" : "0 0 0 2px rgba(24, 144, 255, 0.2)")};
    }
  }
  span {
    flex: 1;
    font-weight: 500;
    color: ${(props) => (props.isDisabled ? "#9ca3af" : props.isSelected ? "#1e40af" : "#595959")};
    transition: color 0.2s ease;
    cursor: ${(props) => (props.isDisabled ? "not-allowed" : "pointer")};
  }
`

const MaxSelectionMessage = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 12px 16px;
  border-radius: 6px;
  margin-bottom: 16px;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  animation: fadeIn 0.3s ease;
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`

const SelectionSummaryBar = styled.div`
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 16px;
  margin-bottom: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const SelectionCountText = styled.div`
  font-size: 14px;
  color: #374151;
  font-weight: 500;
`

const SelectionCount = styled.span`
  font-weight: 600;
  color: ${(props) => (props.count >= 5 ? "#dc2626" : "#3b82f6")};
`

const ClearAllButton = styled.button`
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border: 1px solid #d9d9d9;
  color: #595959;
  font-size: 12px;
  font-weight: 500;
  padding: 4px 16px;
  height: 32px;
  border-radius: 4px;
  &:hover {
    background: #f9fafb;
    border-color: #9ca3af;
  }
`

// Loading, error, and empty state components
const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
  background: #ffffff;
  border-radius: 8px;
  border: 1px solid #e8e8e8;
  margin: 20px 0;
  .loading-text {
    margin-top: 16px;
    color: #8c8c8c;
    font-size: 14px;
    font-weight: 500;
  }
`

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
  background: #fff2f0;
  border-radius: 8px;
  border: 1px solid #ffccc7;
  margin: 20px 0;
  .error-title {
    font-size: 16px;
    font-weight: 600;
    color: #cf1322;
    margin-bottom: 8px;
  }
  .error-description {
    font-size: 14px;
    color: #8c8c8c;
    margin-bottom: 16px;
    line-height: 1.4;
  }
`

const EmptyContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
  background: #fafafa;
  border-radius: 8px;
  border: 1px solid #e8e8e8;
  margin: 20px 0;
  min-height: 200px;
  .empty-title {
    font-size: 16px;
    font-weight: 600;
    color: #595959;
    margin-bottom: 8px;
  }
  .empty-description {
    font-size: 14px;
    color: #8c8c8c;
    line-height: 1.4;
    margin-bottom: 16px;
  }
`

const CategoryHeader = styled.div`
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f0f0f0;
`

const CategoryTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #262626;
  margin: 0 0 8px 0;
`

const CategoryDescription = styled.p`
  font-size: 13px;
  color: #8c8c8c;
  margin: 0;
  line-height: 1.4;
`

const ModalFooter = styled.div`
  padding: 20px 24px;
  border-top: 1px solid #f0f0f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #fafafa;
  flex-shrink: 0;
  @media (max-width: 768px) {
    padding: 16px 20px;
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
  }
`

const FooterLeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 8px;
    align-items: stretch;
  }
`

const FooterRightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  @media (max-width: 768px) {
    width: 100%;
    justify-content: space-between;
  }
`

const FooterSelectionSummary = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  @media (max-width: 768px) {
    justify-content: space-between;
  }
`

const SummaryText = styled.div`
  font-size: 14px;
  color: #595959;
  font-weight: 500;
  @media (max-width: 768px) {
    text-align: center;
  }
`

const ContinueBtn = styled(Button)`
  background: linear-gradient(135deg, #1890ff 0%, #40a9ff 100%);
  border: none;
  color: #ffffff;
  font-weight: 600;
  height: 40px;
  padding: 0 32px;
  border-radius: 6px;
  font-size: 14px;
  min-width: 140px;
  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #40a9ff 0%, #69c0ff 100%);
    color: #ffffff;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(24, 144, 255, 0.3);
  }
  &:focus:not(:disabled) {
    background: linear-gradient(135deg, #40a9ff 0%, #69c0ff 100%);
    color: #ffffff;
  }
  &:disabled {
    background: #f5f5f5;
    border: 1px solid #d9d9d9;
    color: #bfbfbf;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
  @media (max-width: 768px) {
    width: 100%;
    min-width: auto;
  }
`

const SummaryCard = styled.div`
  background: ${(props) => props.bgColor || "#f8fafc"};
  border: 1px solid ${(props) => props.borderColor || "#e5e7eb"};
  border-radius: 6px;
  padding: 16px;
  text-align: center;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  @media (max-width: 768px) {
    padding: 12px;
  }
`

const CardTitle = styled.div`
  font-size: 12px;
  color: #8c8c8c;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
`

const CardValue = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: ${(props) => props.color || "#262626"};
  line-height: 1.2;
  @media (max-width: 768px) {
    font-size: 16px;
  }
`

const ViewMoreButton = styled(Button)`
  width: fit-content;
  margin: 16px auto 0 auto;
  height: 40px;
  border: 1px solid #d9d9d9;
  background: #ffffff;
  color: #595959;
  font-weight: 500;
  display: block;
  padding: 0 24px;
  &:hover:not(:disabled) {
    border-color: #1890ff;
    color: #1890ff;
    background: #f0f8ff;
  }
  &:disabled {
    background: #f5f5f5;
    border-color: #d9d9d9;
    color: #bfbfbf;
  }
`

// Practice selection interface - Sidebar with topic indicators
const SidebarTabWithIndicator = ({ category, label, isActive, onClick, selectedTopics }) => {
  const selectedCount = selectedTopics[category]?.length || 0
  const hasSelectedTopics = selectedCount > 0

  return (
    <SidebarTab active={isActive} hasSelectedTopics={hasSelectedTopics} selectedCount={selectedCount} onClick={onClick}>
      {label}
    </SidebarTab>
  )
}

// Aptitude component
const Aptitude = ({ auth, onAssessmentAssigned, assignedAssessment }) => {
  // State management
  const [loading, setLoading] = useState(false)
  const [retakeLoading, setRetakeLoading] = useState(false)
  const [scoreDrawerVisible, setScoreDrawerVisible] = useState(false)
  const [selectedAssessment, setSelectedAssessment] = useState(null)
  const [reportsLoading, setReportsLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)

  // Assessment flow states
  const [currentView, setCurrentView] = useState("practice-dashboard")
  const [practiceAssessment, setPracticeAssessment] = useState(null)
  const [assessmentStarted, setAssessmentStarted] = useState(false)
  const [assessmentCompleted, setAssessmentCompleted] = useState(false)
  const [completionData, setCompletionData] = useState(null)

  // Refs to prevent multiple API calls
  const practiceHistoryFetched = useRef(false)
  const reportsFetched = useRef(false)
  const retakeEligibilityFetched = useRef(false)
  const practiceScoresFetched = useRef(false)

  const dispatch = useDispatch()
  const user = useSelector((state) => state.auth.user)
  const authStudentId = useSelector((state) => state.auth?.studentId)
  const studentDetails = useSelector((state) => state.studentData?.studentDetails)
  const authStudentEmail = studentDetails?.admin?.email

  // Redux selectors - Filter for Aptitude type assessments
  const allPracticeScores = useSelector((state) => state.assessment?.practiceScores?.data || [])
  const practiceScores = allPracticeScores.filter((score) => {
    const type = (score.type || score.assessmentType || "").toLowerCase()
    return type === "aptitude"
  })

  const practiceHistory = useSelector(selectors.getAptitudePracticeHistory)
  const allPracticeHistory = useSelector(selectors.getPracticeHistory)
  const historyLoading = useSelector(selectors.getPracticeHistoryLoading)
  const historyError = useSelector(selectors.getPracticeHistoryError)
  const practiceHistoryPagination = useSelector(selectors.getPracticeHistoryPagination)

  const reports = useSelector(selectors.getReports)
  const reportsError = useSelector(selectors.getReportsError)
  const retakeEligibility = useSelector(selectors.getRetakeEligibility)
  const retakeEligibilityLoading = useSelector(selectors.getRetakeEligibilityLoading)
  const retakeEligibilityError = useSelector(selectors.getRetakeEligibilityError)

  const aptitudePracticeScores = useSelector((state) => state.assessment?.aptitudePracticeScores?.data || [])
  const practiceScoresLoading = useSelector((state) => state.assessment?.aptitudePracticeScores?.loading || false)
  const practiceScoresError = useSelector((state) => state.assessment?.aptitudePracticeScores?.error || null)
  const aptitudeDayZero = useSelector(selectAptitudeDayZero)

  // Enhanced error handling functions
  const showErrorMessage = (error, context = "") => {
    const errorMessage = error?.message || error?.toString() || "An unexpected error occurred"
    message.error(`${context ? context + ": " : ""}${errorMessage}`)
    console.error(`Error ${context}:`, error)
  }

  const showSuccessMessage = (text) => {
    message.success(text)
  }

  const showLoadingMessage = (text) => {
    return message.loading(text, 0)
  }

  // Assessment utility functions
  const getMostRecentAssessment = () => {
    if (practiceHistory && practiceHistory.length > 0) {
      return practiceHistory[0]
    }
    return null
  }

  // const checkRetakeEligibilityForAssessment = async (assessmentAssignedId) => {
  //   try {
  //     console.log("🔄 Checking retake eligibility for aptitude assessment:", assessmentAssignedId)
  //     const eligibilityData = await dispatch(checkRetakeEligibility(assessmentAssignedId))
  //     console.log("✅ Aptitude retake eligibility check completed:", eligibilityData)
  //     return eligibilityData
  //   } catch (error) {
  //     console.error("❌ Failed to check aptitude retake eligibility:", error)
  //     throw error
  //   }
  // }

  const isRetakeAvailable = (assessment) => {
    // if (!assessment || !assessment.scoresCalculated) {
    //   return false
    // }
    // const eligibilityData = retakeEligibility
    // if (eligibilityData && eligibilityData.assessmentAssignedId === assessment.assessment_assigned_id) {
    //   return eligibilityData.canRetake
    // }
    return true
  }

  // Chart data preparation for Aptitude assessments
  const generateChartData = () => {
    console.log("chartData Aptitude===================", aptitudePracticeScores)
    console.log("dayZero Aptitude===================", aptitudeDayZero)

    // Return demo data if no real data available
    if (!aptitudePracticeScores || aptitudePracticeScores.length === 0) {
      // If we have dayZero data but no practice scores, create a chart with just dayZero
      if (aptitudeDayZero) {
        const dayZeroScores = {
          quantitative: aptitudeDayZero.category_scores?.Quantitative || 0,
          logical: aptitudeDayZero.category_scores?.["Logical Reasoning"] || 0,
          critical: aptitudeDayZero.category_scores?.["Critical Reasoning"] || 0,
        }

        return [{
          session: 0,
          quantitative_score: dayZeroScores.quantitative,
          logical_score: dayZeroScores.logical,
          critical_score: dayZeroScores.critical,
          quantitative_ema: dayZeroScores.quantitative,
          logical_ema: dayZeroScores.logical,
          critical_ema: dayZeroScores.critical,
          total_score: aptitudeDayZero.total_score || 0,
          combined_progress: aptitudeDayZero.total_score || 0,
          submittedAt: null,
          assessmentAssignedId: null,
          delta: 0,
          isOnlyDayZero: true,
        }]
      }

      // Otherwise return demo data
      return demoChartData.map((item, index) => ({
        session: index,
        quantitative_score: item.Quantitative,
        logical_score: item.Logical,
        critical_score: item.Critical,
        quantitative_ema: item.Quantitative,
        logical_ema: item.Logical,
        critical_ema: item.Critical,
        total_score: item["Combined Progress"],
        combined_progress: item["Combined Progress"],
        submittedAt:
          index === 0
            ? null
            : new Date(Date.now() - (demoChartData.length - index) * 24 * 60 * 60 * 1000).toISOString(),
        assessmentAssignedId: index === 0 ? null : `demo_${index}`,
        delta: 0,
      }))
    }

    const sessions = []

    // Group scores by unique assessment sessions (by assessment_assigned_id and submittedAt)
    const uniqueSessions = aptitudePracticeScores.reduce((acc, scoreData) => {
      const sessionKey = `${scoreData.assessment_assigned_id}_${scoreData.submittedAt}`
      if (!acc[sessionKey]) {
        acc[sessionKey] = scoreData
      }
      return acc
    }, {})

    const sortedSessions = Object.values(uniqueSessions).sort(
      (a, b) => new Date(a.submittedAt) - new Date(b.submittedAt),
    )

    // Add baseline session using dayZero data if available
    if (aptitudeDayZero) {
      const dayZeroScores = {
        quantitative: aptitudeDayZero.category_scores?.Quantitative || 0,
        logical: aptitudeDayZero.category_scores?.["Logical Reasoning"] || 0,
        critical: aptitudeDayZero.category_scores?.["Critical Reasoning"] || 0,
      }

      sessions.push({
        session: 0,
        quantitative_score: dayZeroScores.quantitative,
        logical_score: dayZeroScores.logical,
        critical_score: dayZeroScores.critical,
        quantitative_ema: dayZeroScores.quantitative,
        logical_ema: dayZeroScores.logical,
        critical_ema: dayZeroScores.critical,
        total_score: aptitudeDayZero.total_score || 0,
        combined_progress: aptitudeDayZero.total_score || 0,
        submittedAt: null,
        assessmentAssignedId: null,
        delta: 0,
      })
    } else {
      // Add baseline session with zeros if no dayZero data
      sessions.push({
        session: 0,
        quantitative_score: 0,
        logical_score: 0,
        critical_score: 0,
        quantitative_ema: 0,
        logical_ema: 0,
        critical_ema: 0,
        total_score: 0,
        combined_progress: 0,
        submittedAt: null,
        assessmentAssignedId: null,
        delta: 0,
      })
    }

    sortedSessions.forEach((scoreData, index) => {
      // Extract scores from category_scores array
      const scores = {
        quantitative: 0,
        logical: 0,
        critical: 0,
        delta: scoreData.delta || 0,
      }

      // Map category scores to our expected format
      scoreData.category_scores?.forEach((category) => {
        const categoryName = category.name.toLowerCase()
        if (categoryName.includes("quantitative")) {
          scores.quantitative = category.percent || 0
        } else if (categoryName.includes("logical")) {
          scores.logical = category.percent || 0
        } else if (categoryName.includes("critical")) {
          scores.critical = category.percent || 0
        }
      })

      // Use the direct scores from backend (no EMA calculation)
      sessions.push({
        session: index + 1, // Start from 1 instead of 0
        quantitative_score: scores.quantitative,
        logical_score: scores.logical,
        critical_score: scores.critical,
        quantitative_ema: scores.quantitative, // Use direct score
        logical_ema: scores.logical, // Use direct score
        critical_ema: scores.critical, // Use direct score
        total_score: scoreData.total_score,
        combined_progress: scoreData.total_score, // Use direct total score
        submittedAt: scoreData.submittedAt,
        assessmentAssignedId: scoreData.assessment_assigned_id,
        delta: scores.delta,
      })
    })

    return sessions
  }

  const hasOnlyDayZeroData = () => {
    const chartData = generateChartData()
    // Check if we only have one data point (day 0) and no practice scores
    return (
      chartData.length === 1 &&
      (!practiceScores || practiceScores.length === 0) &&
      aptitudeDayZero
    )
  }

  const shouldShowDemoChart = () => {
    // Show demo chart when no real data at all
    return (
      (!practiceScores || practiceScores.length === 0) && !aptitudeDayZero
    )
  }

  const shouldBlurChart = () => {
    // Blur chart when we only have day 0 data or showing demo data
    return hasOnlyDayZeroData() || shouldShowDemoChart()
  }


  // Enhanced analytics calculations
  const calculateAdvancedAnalytics = () => {
    const chartData = generateChartData()
    if (!chartData || chartData.length === 0) {
      return {
        averageImprovement: 0,
        strongestSkill: "None",
        weakestSkill: "None",
        consistencyScore: 0,
        totalPracticeTime: 0,
        skillTrends: {},
      }
    }

    const skills = ["quantitative_ema", "logical_ema", "critical_ema"]
    const skillNames = ["Quantitative", "Logical", "Critical Reasoning"]

    // Calculate averages for each skill using EMA values
    const skillAverages = skills.map((skill) => {
      const scores = chartData.map((session) => session[skill])
      return scores.reduce((sum, score) => sum + score, 0) / scores.length
    })

    // Find strongest and weakest skills
    const maxAvg = Math.max(...skillAverages)
    const minAvg = Math.min(...skillAverages)
    const strongestIndex = skillAverages.indexOf(maxAvg)
    const weakestIndex = skillAverages.indexOf(minAvg)

    // Calculate improvement trend using combined progress (total score EMA)
    const firstSession = chartData[0]
    const lastSession = chartData[chartData.length - 1]
    const averageImprovement = lastSession.combined_progress - firstSession.combined_progress

    // Calculate consistency (lower standard deviation = higher consistency)
    const progressScores = chartData.map((session) => session.combined_progress)
    const mean = progressScores.reduce((sum, score) => sum + score, 0) / progressScores.length
    const variance = progressScores.reduce((sum, score) => sum + Math.pow(score - mean, 2), 0) / progressScores.length
    const consistencyScore = Math.max(0, 100 - Math.sqrt(variance))

    // Calculate total practice time (mock calculation)
    const totalPracticeTime = chartData.length * 45 // Assuming 45 min per session

    return {
      averageImprovement,
      strongestSkill: skillNames[strongestIndex],
      weakestSkill: skillNames[weakestIndex],
      consistencyScore: Math.round(consistencyScore),
      totalPracticeTime,
      skillAverages,
    }
  }

  const prepareChartData = () => {
    const chartData = generateChartData()
    return chartData.map((session) => ({
      session: session.session,
      Quantitative: session.quantitative_ema,
      Logical: session.logical_ema,
      Critical: session.critical_ema,
      "Combined Progress": session.combined_progress,
      delta: session.delta,
    }))
  }

  // Event handlers
  const handleContinue = async () => {
    if (!authStudentEmail) {
      showErrorMessage(null, "Student email not found. Please refresh the page.")
      return
    }

    setLoading(true)
    const loadingMessage = showLoadingMessage("Assigning aptitude practice assessment...")

    try {
      const response = await assignPracticeAssessment({
        studentEmail: authStudentEmail,
        assessmentType: "Aptitude",
        cefrLevel: "",
      })

      if (response && response.data) {
        const newAssessment = {
          assessment_assigned_id: response.data.assessmentAssignedId,
          assessment_set_id: response.data.assessmentSetId,
          assessment_corporate_map_id: response.data.assessmentMapId,
          title: "Aptitude Practice Assessment",
          type: "Aptitude",
          startTime: response.data.startTime,
          endTime: response.data.endTime,
          duration: response.data.duration,
          isPractice: true,
          entityType: "practice",
          topics: response.data.topics,
        }

        handleAssessmentAssigned(newAssessment)
        showSuccessMessage("Aptitude practice assessment assigned successfully!")
      } else {
        throw new Error("Invalid response from server")
      }
    } catch (error) {
      showErrorMessage(error, "Failed to assign aptitude practice assessment")
    } finally {
      message.destroy()
      setLoading(false)
    }
  }

  const handleAISelection = async () => {
    setSelectedTopics({})
    try {
      const response = await assignPracticeAssessment({
        studentEmail: authStudentEmail,
        assessmentType: "Aptitude",
        cefrLevel: "",
        selectedTopics: selectedTopics,
      })

      console.log("🚀 [DEBUG] Response from assignPracticeAssessment:", JSON.stringify(response, null, 2  ))

      if (response && response.data) {
        const newAssessment = {
          assessment_assigned_id: response.data.assessmentAssignedId,
          assessment_set_id: response.data.assessmentSetId,
          assessment_corporate_map_id: response.data.assessmentMapId,
          title: "Aptitude Practice Assessment",
          type: "Aptitude",
          startTime: response.data.startTime,
          endTime: response.data.endTime,
          duration: response.data.duration,
          isPractice: true,
          entityType: "practice",
          selectedTopics: selectedTopics,
          topics: response.data.topics,
        }
        console.log("🚀 [DEBUG] New assessment object:", JSON.stringify(newAssessment, null, 2))
        trackPracticeSessionStarted(newAssessment.assessment_assigned_id, newAssessment.topics, "Aptitude", "AI", false, newAssessment.entityType)
        handleAssessmentAssigned(newAssessment)
        showSuccessMessage("Aptitude practice assessment assigned successfully!")
      } else {
        throw new Error("Invalid response from server")
      }
    } catch (error) {
      showErrorMessage(error, "Failed to assign aptitude practice assessment. Please try again.")
    } finally {
      message.destroy()
      setLoading(false)
    }
  }

  const handleManualSelection = () => {
    setShowModal(true)
  }

  const handleRetakeAssessment = async (assessment) => {
    if (!assessment) {
      showErrorMessage(null, "Assessment not found")
      return
    }

    console.log("🔄 === APTITUDE RETAKE ASSESSMENT HANDLER START ===")
    console.log("🔄 Aptitude retake button clicked for assessment:", assessment.id)

    const assessmentId = assessment.assessment_assigned_id || assessment.id
    setRetakeLoading(true)
    // const loadingMessage = showLoadingMessage("Starting aptitude retake assessment...")

    try {
      // console.log("🔄 Step 1: Checking aptitude retake eligibility...")
      // const eligibilityData = await checkRetakeEligibilityForAssessment(assessmentId)

      // if (!eligibilityData.canRetake) {
      //   console.error("❌ Aptitude retake not eligible:", eligibilityData.reason)
      //   throw new Error(`Retake not available: ${eligibilityData.reason}`)
      // }

      // console.log("✅ Step 1 SUCCESS: Aptitude retake eligibility confirmed")
      // console.log("🔄 Step 2: Creating aptitude retake assessment object...")

      const retakeAssessment = {
        assessment_assigned_id: assessmentId,
        assessment_set_id: assessment.assessment_set_id,
        assessment_corporate_map_id: assessment.assessment_corporate_map_id,
        title: "Aptitude Practice Assessment (Retake)",
        type: "Aptitude",
        startTime: assessment.startTime,
        endTime: assessment.endTime,
        duration: assessment.duration,
        isPractice: true,
        entityType: "practice",
        isRetake: true,
        originalAssessmentId: assessment.id,
        topics: assessment.topics,
      }

      console.log("✅ Step 2 SUCCESS: Aptitude retake assessment object created")
      console.log("🔄 Aptitude retake assessment object:", retakeAssessment)

      setPracticeAssessment(retakeAssessment)
      setCurrentView("instructions")
      localStorage.setItem("isPracticeAssessment", "true")

      showSuccessMessage("Starting retake of your most recent aptitude assessment!")

      console.log("🎉 === APTITUDE RETAKE ASSESSMENT HANDLER COMPLETED SUCCESSFULLY ===")
    } catch (error) {
      console.error("❌ === APTITUDE RETAKE ASSESSMENT HANDLER ERROR ===")
      console.error("❌ Error details:", error)
      showErrorMessage(error, "Failed to start aptitude retake assessment")
    } finally {
      message.destroy()
      setRetakeLoading(false)
      console.log("🔄 === APTITUDE RETAKE ASSESSMENT HANDLER END ===")
    }
  }

  const handleViewScore = async (assessment) => {
    const assessmentWithType = {
      ...assessment,
      type: "Aptitude",
    }
    setSelectedAssessment(assessmentWithType)
    setScoreDrawerVisible(true)

    const assessmentAssignedId = assessment.assessment_assigned_id || assessment.id
    if (!assessmentAssignedId) {
      showErrorMessage(null, "Assessment ID not found")
      return
    }
  }

  const handleCloseScoreDrawer = () => {
    setScoreDrawerVisible(false)
    setSelectedAssessment(null)
  }

  // ponytail: dead prop - score-drawer does not render a download button
  const handleDownloadReport = (assessmentId) => {
    return dispatch(generatePDFReport(assessmentId))
  }

  const handleViewDetailedReport = (assessmentId) => {
    message.info("Detailed report functionality will be implemented soon.")
  }

  const handleLoadMore = async () => {
    if (!authStudentId || !practiceHistoryPagination?.hasNextPage || loadingMore) {
      return
    }

    setLoadingMore(true)
    try {
      const nextPage = practiceHistoryPagination.nextPage
      console.log(`🔍 Loading more aptitude practice assessments - Page ${nextPage}`)
      await dispatch(fetchAptitudePracticeAssessments(authStudentId, nextPage, 5))
      console.log(`✅ Successfully loaded aptitude page ${nextPage}`)
    } catch (error) {
      console.error("❌ Failed to load more aptitude practice assessments:", error)
      showErrorMessage(error, "Failed to load more assessments")
    } finally {
      setLoadingMore(false)
    }
  }

  const handleRetryFetch = () => {
    practiceHistoryFetched.current = false
    reportsFetched.current = false
    practiceScoresFetched.current = false
    retakeEligibilityFetched.current = false

    if (authStudentId) {
      dispatch(fetchAptitudePracticeAssessments(authStudentId, 1, 5))
    }
    if (authStudentEmail) {
      dispatch(fetchAptitudePracticeScores(authStudentEmail))
    }
  }

  // Assessment flow handlers
  const handleAssessmentAssigned = (assessment) => {
    setPracticeAssessment(assessment)
    setCurrentView("instructions")
    setAssessmentStarted(true)
  }

  const handleAssessmentComplete = (completionData) => {
    setCompletionData(completionData)
    setCurrentView("completion")
    setAssessmentCompleted(true)
    setAssessmentStarted(false)
  }

  const handleBackToPracticeDashboard = () => {
    setCurrentView("practice-dashboard")
    setPracticeAssessment(null)
    setAssessmentStarted(false)
    setAssessmentCompleted(false)
    setCompletionData(null)
    localStorage.removeItem("isPracticeAssessment")
    handleRetryFetch()
  }

  // Topic selection state and handlers
  const [showModal, setShowModal] = useState(false)
  const [activeTab, setActiveTab] = useState("quantitative")
  const [selectedTopics, setSelectedTopics] = useState({
    quantitative: [],
    logical: [],
    critical: [],
  })

  const [showMaxSelectionMessage, setShowMaxSelectionMessage] = useState(false)
  const MAX_TOPICS_ALLOWED = 5

  // Topic data with descriptions
  const topicsData = {
    quantitative: {
      title: "Quantitative Aptitude",
      description: "Mathematical and numerical reasoning skills",
      topics: [
        "Number System",
        "Time, Speed, and Distance",
        "Time and Work",
        "Profit and Loss",
        "Percentages",
        "Ratio & Proportion",
        "Pipes and Cisterns",
        "Averages",
        "Permutations & Combinations (P&C)",
        "Simple & Compound Interest",
        "Mixtures and Alligations",
        "Probability",
      ],
    },
    logical: {
      title: "Logical Reasoning",
      description: "Pattern recognition and logical thinking",
      topics: [
        "Coding-Decoding",
        "Seating Arrangement",
        "Puzzles (Logic Puzzles)",
        "Blood Relations",
        "Number/Letter Series",
        "Direction Sense",
        "Syllogisms",
        "Input-Output",
        "Data Sufficiency",
        "Analogy",
        "Odd One Out",
      ],
    },
    critical: {
      title: "Critical Reasoning",
      description: "Analytical and critical thinking abilities",
      topics: [
        "Statement & Assumptions",
        "Conclusions & Inferences",
        "Statement & Arguments",
        "Course of Action",
        "Cause & Effect",
        "Facts, Inferences & Judgements",
        "Paradox Questions",
      ],
    },
  }

  const handleTopicToggle = (category, topic) => {
    if (!category || !topic) return

    setSelectedTopics((prev) => {
      const isCurrentlySelected = prev[category]?.includes(topic) || false
      const currentTotalCount = getTotalSelectedTopics()

      if (isCurrentlySelected) {
        setShowMaxSelectionMessage(false)
        return {
          ...prev,
          [category]: (prev[category] || []).filter((t) => t !== topic),
        }
      } else {
        if (currentTotalCount >= MAX_TOPICS_ALLOWED) {
          setShowMaxSelectionMessage(true)
          setTimeout(() => {
            setShowMaxSelectionMessage(false)
          }, 3000)
          return prev
        }

        setShowMaxSelectionMessage(false)
        return {
          ...prev,
          [category]: [...(prev[category] || []), topic],
        }
      }
    })
  }

  const getTotalSelectedTopics = () => {
    return Object.values(selectedTopics).reduce((total, topics) => total + (topics?.length || 0), 0)
  }

  const getSelectedSections = () => {
    return Object.values(selectedTopics).filter((topics) => topics && topics.length > 0).length
  }

  const isTopicSelectionDisabled = (category, topic) => {
    if (!category || !topic || !selectedTopics[category]) return false

    const isCurrentlySelected = selectedTopics[category].includes(topic)
    const currentTotalCount = getTotalSelectedTopics()

    return !isCurrentlySelected && currentTotalCount >= MAX_TOPICS_ALLOWED
  }

  const handleClearAllTopics = () => {
    setSelectedTopics({
      quantitative: [],
      logical: [],
      critical: [],
    })
    setShowMaxSelectionMessage(false)
  }

  const handleContinueWithTopics = async (topics = selectedTopics) => {
    const totalSelected = Object.values(topics).reduce((total, topicArray) => total + topicArray.length, 0)

    if (totalSelected === 0) {
      message.warning("Please select at least one topic to continue.")
      return
    }

    if (totalSelected > MAX_TOPICS_ALLOWED) {
      message.warning(`Please select maximum ${MAX_TOPICS_ALLOWED} topics to continue.`)
      return
    }

    if (!authStudentEmail) {
      showErrorMessage(null, "Student email not found. Please refresh the page.")
      return
    }

    setLoading(true)
    setShowModal(false)
    const loadingMessage = showLoadingMessage("Assigning aptitude practice assessment...")

    try {
      const response = await assignPracticeAssessment({
        studentEmail: authStudentEmail,
        assessmentType: "Aptitude",
        cefrLevel: "",
        selectedTopics: topics,
      })

      if (response && response.data) {
        const newAssessment = {
          assessment_assigned_id: response.data.assessmentAssignedId,
          assessment_set_id: response.data.assessmentSetId,
          assessment_corporate_map_id: response.data.assessmentMapId,
          title: "Aptitude Practice Assessment",
          type: "Aptitude",
          startTime: response.data.startTime,
          endTime: response.data.endTime,
          duration: response.data.duration,
          isPractice: true,
          entityType: "practice",
          selectedTopics: response.data.topics,
        }

        trackPracticeSessionStarted(newAssessment.assessment_assigned_id, newAssessment.selectedTopics, "Aptitude", "Manual", false, newAssessment.entityType)
        handleAssessmentAssigned(newAssessment)
        showSuccessMessage("Aptitude practice assessment assigned successfully!")
      } else {
        throw new Error("Invalid response from server")
      }
    } catch (error) {
      showErrorMessage(error, "Failed to assign aptitude practice assessment")
    } finally {
      message.destroy()
      setLoading(false)
    }
  }

  // Effects
  useEffect(() => {
    if (authStudentEmail && !practiceScoresFetched.current) {
      practiceScoresFetched.current = true
      dispatch(fetchAptitudePracticeScores(authStudentEmail)).catch((error) => {
        console.error("Failed to fetch aptitude practice scores:", error)
        showErrorMessage(error, "Failed to load practice scores")
        practiceScoresFetched.current = false
      })
    }
  }, [authStudentEmail, dispatch])

  useEffect(() => {
    if (authStudentId && !practiceHistoryFetched.current) {
      practiceHistoryFetched.current = true
      console.log("🔄 Fetching aptitude practice assessments for student:", authStudentId)
      dispatch(fetchAptitudePracticeAssessments(authStudentId, 1, 5)).catch((error) => {
        console.error("Failed to fetch aptitude practice history:", error)
        showErrorMessage(error, "Failed to load practice history")
        practiceHistoryFetched.current = false
      })
    }
  }, [authStudentId, dispatch])

  useEffect(() => {
    if (practiceHistory && practiceHistory.length > 0 && !reportsFetched.current) {
      const assessmentIds = practiceHistory
        .map((assessment) => assessment.assessment_assigned_id || assessment.id)
        .filter((id) => id)

      if (assessmentIds.length > 0) {
        reportsFetched.current = true
        setReportsLoading(true)
        dispatch(fetchReports(assessmentIds))
          .catch((error) => {
            console.error("Error fetching aptitude reports:", error)
            showErrorMessage(error, "Failed to load reports")
            reportsFetched.current = false
          })
          .finally(() => {
            setReportsLoading(false)
          })
      }
    }
  }, [practiceHistory, dispatch])

  // useEffect(() => {
  //   const recentAssessment = getMostRecentAssessment()
  //   if (recentAssessment && recentAssessment.scoresCalculated && !retakeEligibilityFetched.current) {
  //     const assessmentAssignedId = recentAssessment.assessment_assigned_id
  //     console.log("🔄 Checking retake eligibility for most recent aptitude assessment:", assessmentAssignedId)
  //     retakeEligibilityFetched.current = true
  //     dispatch(checkRetakeEligibility(assessmentAssignedId))
  //       .then((eligibilityData) => {
  //         console.log("✅ Aptitude retake eligibility check completed:", eligibilityData)
  //       })
  //       .catch((error) => {
  //         console.error("❌ Failed to check aptitude retake eligibility:", error)
  //         showErrorMessage(error, "Failed to check retake eligibility")
  //         retakeEligibilityFetched.current = false
  //       })
  //   }
  // }, [practiceHistory, dispatch])

  useEffect(() => {
    retakeEligibilityFetched.current = false
  }, [practiceHistory?.length])

  // Custom tooltip component for charts
  const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const deltaValue = payload[0]?.payload?.delta || 0
    return (
      <div
        style={{
          backgroundColor: "white",
          border: "1px solid #d9d9d9",
          borderRadius: "6px",
          padding: "12px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
        }}
      >
     <span
  style={{
    fontWeight: "600",
    margin: "0 0 8px 0",
    color: "#262626",
    fontSize: "14px",
  }}
>
  {hasRealData ? `Day ${label}` : `Session ${label}`}
  {deltaValue !== 0 && (
    <span
      style={{
        marginLeft: "8px",
        color: deltaValue > 0 ? "#52c41a" : "#ff4d4f",
        fontSize: "12px",
        fontWeight: "500",
        opacity: "0.8",
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
      }}
    >
      {deltaValue > 0 ? "+" : ""}
      {deltaValue.toFixed(1)}%
      {deltaValue > 0 ? (
        <RiseOutlined style={{ fontSize: "16px" }} />
      ) : (
        <FallOutlined style={{ fontSize: "16px" }} />
      )}
    </span>
  )}
</span>

        {payload.map((entry, index) => {
          const icons = {
            Quantitative: "",
            Logical: "",
            Critical: "",
            "Combined Progress": "",
          }
          return (
            <p
              key={index}
              style={{
                margin: "4px 0",
                color: entry.color,
                fontSize: "13px",
                fontWeight: "500",
              }}
            >
              <span style={{ marginRight: "8px" }}>{icons[entry.dataKey] || ""}</span>
              {`${entry.dataKey}: ${entry.value.toFixed(1)}%`}
            </p>
          )
        })}
      </div>
    )
  }
  return null
}

  // Utility functions
  const formatAssessmentDate = (dateString) => {
    return moment(dateString).format("MMM DD, YYYY")
  }

  const chartData = generateChartData()
  const finalCombinedProgress = chartData[chartData.length - 1]?.combined_progress || 0

  // Check if we have real data or should show demo
  const hasRealData = (aptitudePracticeScores && aptitudePracticeScores.length > 0) || aptitudeDayZero
  const hasHistoryData = practiceHistory && practiceHistory.length > 0

  // Render different views based on current state
  const renderCurrentView = () => {
    switch (currentView) {
      case "instructions":
        return (
          <AptitudeInstructions
            assessment={practiceAssessment}
            questions={[]}
            totalQuestions={0}
            error={null}
            responses={{}}
            saveQuestionResponse={() => {}}
            studentId={authStudentId}
            setAssessment={handleAssessmentComplete}
          />
        )
      case "assessment":
        return <AptitudeAssessment setAssessment={handleAssessmentComplete} />
      case "completion":
        return (
          <AptitudeCompletion
            assessmentData={completionData}
            submittedTime={completionData?.submittedTime}
            setAssessment={handleBackToPracticeDashboard}
            isPractice={true}
          />
        )
      default:
        return (
          <StyledAptitudeContainer>
            <VisualizationSection className={practiceScoresLoading ? "has-overlay" : ""}>
              <div className="section-content">
                <Row gutter={24}>
                  <Col span={24}>
                    <Card
                      title="Progress Analytics"
                      bordered={false}
                      headStyle={{
                        fontSize: "16px",
                        fontWeight: 600,
                        color: "#262626",
                      }}
                    >
                      {practiceScoresError ? (
                        <ErrorContainer>
                          <div className="error-title">Failed to load progress data</div>
                          <div className="error-description">{practiceScoresError}</div>
                          <Button type="primary" icon={<ReloadOutlined />} onClick={handleRetryFetch}>
                            Retry
                          </Button>
                        </ErrorContainer>
                      ) : (
                        <>
                          <ChartContainer
                            className={shouldBlurChart() ? 'demo-mode' : ''}
                            hasOnlyDayZero={hasOnlyDayZeroData()}
                          >
                            <ResponsiveContainer width="100%" height={400}>
                              <LineChart
                                data={prepareChartData()}
                                margin={{
                                  top: 20,
                                  right: 30,
                                  left: 20,
                                  bottom: 20,
                                }}
                              >
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis
                                  dataKey="session"
                                  type="number"
                                  domain={["dataMin", "dataMax"]}
                                  tickCount={Math.min(chartData.length, 10)}
                                  stroke="#8c8c8c"
                                  fontSize={12}
                                  tickLine={false}
                                  axisLine={{ stroke: "#d9d9d9", strokeWidth: 1 }}
                                  label={{ value: 'Days', position: 'insideBottom', offset: -3, style: { textAnchor: 'middle', fill: '#595959', fontSize: 12, fontWeight: 500, fontWeight: 'bold' } }}
                                />
                                <YAxis
                                  domain={[0, 100]}
                                  stroke="#8c8c8c"
                                  fontSize={12}
                                  tickLine={false}
                                  axisLine={{ stroke: "#d9d9d9", strokeWidth: 1 }}
                                  label={{ value: 'Scores', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fill: '#595959', fontSize: 12, fontWeight: 500, fontWeight: 'bold' } }}
                                />
                                <RechartsTooltip content={<CustomTooltip />} />
                                <Legend
                                  wrapperStyle={{
                                    paddingTop: "16px",
                                    fontSize: 12,
                                  }}
                                  iconType="line"
                                />
                                <Line
                                  type="monotone"
                                  dataKey="Quantitative"
                                  stroke="#1890ff"
                                  strokeWidth={2}
                                  strokeDasharray="4 4"
                                  dot={{ fill: "#1890ff", strokeWidth: 2, r: 4 }}
                                  activeDot={{ r: 6, fill: "#1890ff" }}
                                  name="Quantitative"
                                  connectNulls={false}
                                />
                                <Line
                                  type="monotone"
                                  dataKey="Logical"
                                  stroke="#fa8c16"
                                  strokeWidth={2}
                                  strokeDasharray="8 4"
                                  dot={{ fill: "#fa8c16", strokeWidth: 2, r: 4 }}
                                  activeDot={{ r: 6, fill: "#fa8c16" }}
                                  name="Logical"
                                  connectNulls={false}
                                />
                                <Line
                                  type="monotone"
                                  dataKey="Critical"
                                  stroke="#eb2f96"
                                  strokeWidth={2}
                                  strokeDasharray="6 6"
                                  dot={{ fill: "#eb2f96", strokeWidth: 2, r: 4 }}
                                  activeDot={{ r: 6, fill: "#eb2f96" }}
                                  name="Critical"
                                  connectNulls={false}
                                />
                                <Line
                                  type="monotone"
                                  dataKey="Combined Progress"
                                  stroke="#52c41a"
                                  strokeWidth={3}
                                  dot={{ fill: "#52c41a", strokeWidth: 3, r: 6 }}
                                  activeDot={{ r: 8, fill: "#52c41a" }}
                                  name="Combined Progress"
                                  connectNulls={false}
                                />
                              </LineChart>
                            </ResponsiveContainer>

                            {hasOnlyDayZeroData() && aptitudeDayZero && (
                              <div
                                className="baseline-overlay"
                                style={{ maxWidth: '500px' }}
                              >
                              <div
                                className="title"
                                style={{ fontSize: '16px', marginBottom: '8px' }}
                              >
                                No Progress Data Available
                              </div>
                              <div
                                className="description"
                                style={{ fontSize: '13px', marginBottom: '12px' }}
                              >
                                Start practicing to see the progress here
                              </div>
                              <div
                                className="scores"
                                style={{ fontSize: '13px', gap: '4px' }}
                              >
                                <div
                                  style={{ marginBottom: '4px', color: '#8c8c8c' }}
                                >
                                  Your scores from diagnosis:
                                </div>
                                <div className="score-item">
                                  <span>Quantitative Reasoning:</span>
                                  <span>{aptitudeDayZero.category_scores?.Quantitative || 0}</span>
                                </div>
                                <div className="score-item">
                                  <span>Logical Reasoning:</span>
                                  <span>{aptitudeDayZero.category_scores?.["Logical Reasoning"] || 0}</span>
                                </div>
                                <div className="score-item">
                                  <span>Critical Reasoning:</span>
                                  <span>{aptitudeDayZero.category_scores?.["Critical Reasoning"] || 0}</span>
                                </div>
                                <div className="score-item total">
                                  <span>Total:</span>
                                  <span>{aptitudeDayZero.total_score}</span>
                                </div>
                              </div>
                            </div>
                          )}
                          </ChartContainer>
                        </>
                      )}
                    </Card>
                  </Col>
                </Row>
              </div>

              {practiceScoresLoading && (
                <SectionOverlay>
                  <Spin size="large" className="overlay-spinner" />
                  {/* <Spinner */}
                  <span className="overlay-text">Loading your progress data...</span>
                </SectionOverlay>
              )}
            </VisualizationSection>

            {/* Practice Selection Section */}
            <PracticeSelectionSection className={loading ? "has-overlay" : ""}>
              <div className="section-content">
                <PracticeHeader>
                  <PracticeTitle>Aptitude Practice</PracticeTitle>
                  <PracticeSubtitle>Choose how you'd like to practice aptitude questions</PracticeSubtitle>
                </PracticeHeader>
                <OptionsContainer>
                  <OptionBox onClick={handleAISelection}>
                    <RecommendedBadge>recommended</RecommendedBadge>
                    <OptionTitle>Let AI pick sections for you</OptionTitle>
                    <OptionDescription>
                      Get a balanced mix of questions from different aptitude sections based on your skill level
                    </OptionDescription>
                  </OptionBox>
                  <OptionBox onClick={handleManualSelection}>
                    <OptionTitle>Let me choose manually</OptionTitle>
                    <OptionDescription>Select specific topics and sections you want to practice</OptionDescription>
                  </OptionBox>
                </OptionsContainer>
              </div>

              {loading && (
                <SectionOverlay>
                  <Spin size="large" className="overlay-spinner" />
                  <span className="overlay-text">Assigning assessment...</span>
                </SectionOverlay>
              )}
            </PracticeSelectionSection>

            {/* Topic Selection Modal */}
            {showModal && (
              <ModalOverlay onClick={() => setShowModal(false)}>
                <ModalContainer onClick={(e) => e.stopPropagation()}>
                  <ModalHeader>
                    <ModalTitle>Select Topics to Practice</ModalTitle>
                    <CloseButton onClick={() => setShowModal(false)}>
                      <CloseOutlined />
                    </CloseButton>
                  </ModalHeader>
                  <ModalBody>
                    <Sidebar>
                      <SidebarTabWithIndicator
                        category="quantitative"
                        label="Quantitative Aptitude"
                        isActive={activeTab === "quantitative"}
                        onClick={() => setActiveTab("quantitative")}
                        selectedTopics={selectedTopics}
                      />
                      <SidebarTabWithIndicator
                        category="logical"
                        label="Logical Reasoning"
                        isActive={activeTab === "logical"}
                        onClick={() => setActiveTab("logical")}
                        selectedTopics={selectedTopics}
                      />
                      <SidebarTabWithIndicator
                        category="critical"
                        label="Critical Reasoning"
                        isActive={activeTab === "critical"}
                        onClick={() => setActiveTab("critical")}
                        selectedTopics={selectedTopics}
                      />
                    </Sidebar>

                    <MainContent>
                      {showMaxSelectionMessage && (
                        <MaxSelectionMessage>
                          <span>⚠️</span>
                          You can select a maximum of {MAX_TOPICS_ALLOWED} topics to practice.
                        </MaxSelectionMessage>
                      )}

                      {topicsData[activeTab] && (
                        <>
                          <CategoryHeader>
                            <CategoryTitle>{topicsData[activeTab].title}</CategoryTitle>
                            <CategoryDescription>{topicsData[activeTab].description}</CategoryDescription>
                          </CategoryHeader>
                          <TopicsGrid>
                            {(topicsData[activeTab].topics || []).map((topic, index) => {
                              const isSelected = selectedTopics[activeTab]?.includes(topic) || false
                              const isDisabled = isTopicSelectionDisabled(activeTab, topic)
                              return (
                                <TopicItem
                                  key={index}
                                  isSelected={isSelected}
                                  isDisabled={isDisabled}
                                  onClick={(e) => {
                                    e.preventDefault()
                                    e.stopPropagation()
                                    if (!isDisabled) {
                                      handleTopicToggle(activeTab, topic)
                                    }
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    disabled={isDisabled}
                                    onChange={() => {}}
                                    onClick={(e) => {
                                      e.stopPropagation()
                                    }}
                                  />
                                  <span>{topic}</span>
                                </TopicItem>
                              )
                            })}
                          </TopicsGrid>
                        </>
                      )}

                      {!topicsData[activeTab] && (
                        <div style={{ textAlign: "center", padding: "20px" }}>
                          <Text type="secondary">Loading topics...</Text>
                        </div>
                      )}
                    </MainContent>
                  </ModalBody>

                  <ModalFooter>
                    <FooterLeftSection>
                      <FooterSelectionSummary>
                        <SelectionCountText>
                          Topics selected:{" "}
                          <SelectionCount count={getTotalSelectedTopics()}>{getTotalSelectedTopics()}</SelectionCount>{" "}
                          of {MAX_TOPICS_ALLOWED}
                        </SelectionCountText>
                      </FooterSelectionSummary>
                      <SummaryText>
                        <strong>{getSelectedSections()}</strong> sections selected
                      </SummaryText>
                    </FooterLeftSection>

                    <FooterRightSection>
                      {getTotalSelectedTopics() > 0 && (
                        <ClearAllButton onClick={handleClearAllTopics}>Clear All</ClearAllButton>
                      )}
                      <ContinueBtn
                        onClick={() => handleContinueWithTopics()}
                        disabled={
                          getTotalSelectedTopics() === 0 || getTotalSelectedTopics() > MAX_TOPICS_ALLOWED || loading
                        }
                      >
                        {loading ? "Starting..." : "Start Assessment"}
                      </ContinueBtn>
                    </FooterRightSection>
                  </ModalFooter>
                </ModalContainer>
              </ModalOverlay>
            )}

            {/* Practice History Section */}
            <HistorySection className={historyLoading && !hasHistoryData ? "has-overlay" : ""}>
              <div className="section-content">
                <HistoryHeader level={4}>Aptitude Practice History</HistoryHeader>
                {historyError ? (
                  <ErrorContainer>
                    <div className="error-title">Failed to load practice history</div>
                    <div className="error-description">{historyError}</div>
                    <Button type="primary" icon={<ReloadOutlined />} onClick={handleRetryFetch}>
                      Retry
                    </Button>
                  </ErrorContainer>
                ) : hasHistoryData ? (
                  <>
                    <SubHeaderText>
                      You are doing great! You have practiced aptitude {practiceHistory.length} time
                      {practiceHistory.length !== 1 ? "s" : ""} so far
                      {practiceHistoryPagination?.totalCount > practiceHistory.length && (
                        <span style={{ color: "#1890ff", marginLeft: "8px" }}>
                          (Showing {practiceHistory.length} of {practiceHistoryPagination.totalCount})
                        </span>
                      )}
                    </SubHeaderText>
                    {practiceHistory.map((assessment, index) => {
                      const isLatestAssessment = index === 0
                      const isRetakeEligible = isLatestAssessment && isRetakeAvailable(assessment)
                      return (
                        <OldPracticeItem key={assessment.id || index}>
                          <OldPracticeContent>
                            <Col flex="1" style={{ minWidth: 0 }}>
                              <PracticeItemLeft>
                                <BadgeContainer>
                                  <DateBadge>{formatAssessmentDate(assessment.submittedAt)}</DateBadge>
                                  {isLatestAssessment && <LatestBadge>Latest</LatestBadge>}
                                </BadgeContainer>
                                <OldPracticeTitle>{assessment.title}</OldPracticeTitle>
                              </PracticeItemLeft>
                            </Col>
                            <Col flex="none">
                              <ScoresContainer>
                                <ScoreItem>
                                  <div className="label">Duration</div>
                                  <div className="value">{assessment.duration?.toFixed(1) || "N/A"} min</div>
                                </ScoreItem>
                                <ScoreItem>
                                  <div className="label">Status</div>
                                  <div
                                    className="value"
                                    style={{
                                      color: assessment.scoresCalculated ? "#52c41a" : "#fa8c16",
                                    }}
                                  >
                                    {assessment.scoresCalculated ? "Completed" : "Pending"}
                                  </div>
                                </ScoreItem>
                                <ActionButtons>
                                  <ViewScoreButton
                                    type="primary"
                                    size="small"
                                    disabled={!assessment.scoresCalculated}
                                    loading={reportsLoading}
                                    onClick={() => handleViewScore(assessment)}
                                  >
                                    {reportsLoading ? "Loading..." : "View Score"}
                                  </ViewScoreButton>
                                  {isLatestAssessment && assessment.scoresCalculated && (
                                    <Tooltip
                                      title={
                                        retakeEligibilityLoading
                                          ? "Checking eligibility..."
                                          : isRetakeEligible
                                            ? "Retake this aptitude assessment"
                                            : retakeEligibility?.reason || "Retake not available"
                                      }
                                      placement="top"
                                    >
                                      <RetakeButton
                                        size="small"
                                        disabled={!isRetakeEligible || retakeLoading || retakeEligibilityLoading}
                                        loading={retakeLoading}
                                        onClick={() => handleRetakeAssessment(assessment)}
                                      >
                                        {retakeLoading ? "Starting..." : "Retake"}
                                      </RetakeButton>
                                    </Tooltip>
                                  )}
                                </ActionButtons>
                              </ScoresContainer>
                            </Col>
                          </OldPracticeContent>
                        </OldPracticeItem>
                      )
                    })}
                    {practiceHistoryPagination?.hasNextPage && (
                      <ViewMoreButton onClick={handleLoadMore} loading={loadingMore} disabled={loadingMore}>
                        {loadingMore
                          ? "Loading more..."
                          : `View More (${practiceHistoryPagination.totalCount - practiceHistory.length} remaining)`}
                      </ViewMoreButton>
                    )}
                    {loadingMore && (
                      <div style={{ textAlign: "center", padding: "16px" }}>
                        <Spin size="small" />
                        <Text style={{ marginLeft: 8, color: "#8c8c8c" }}>Loading more aptitude assessments...</Text>
                      </div>
                    )}
                  </>
                ) : (
                  <EmptyContainer>
                    <div className="empty-title">No aptitude practice assessments completed yet</div>
                    <div className="empty-description">
                      Complete your first aptitude practice assessment to see your progress here!
                    </div>
                  </EmptyContainer>
                )}
              </div>

              {historyLoading && !hasHistoryData && (
                <SectionOverlay>
                  <Spin size="large" className="overlay-spinner" />
                  <span className="overlay-text">Loading your practice history...</span>
                </SectionOverlay>
              )}
            </HistorySection>

            {/* Score Drawer */}
            {scoreDrawerVisible && (
              <ScoreDrawer
                visible={scoreDrawerVisible}
                onClose={handleCloseScoreDrawer}
                assessment={selectedAssessment}
                reports={reports}
                loading={reportsLoading}
                error={reportsError}
                onDownloadReport={handleDownloadReport}
                onViewDetailedReport={handleViewDetailedReport}
              />
            )}
          </StyledAptitudeContainer>
        )
    }
  }

  return renderCurrentView()
}

export default Aptitude
